export type UserRole = 'ADMIN' | 'DISPATCHER' | 'TECHNICIAN';

export type OrderStatus =
  | 'NEW'
  | 'ASSIGNED'
  | 'EN_ROUTE'
  | 'ARRIVED'
  | 'IN_PROGRESS'
  | 'CLIENT_DECLINED'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED';

export interface User {
  id: string;
  role: UserRole;
  fullName: string;
  phone: string;
  email?: string | null;
  commissionRate?: string;
  isActive?: boolean;
  isGreaseTrapTeam?: boolean;
  forceNightOrdersEnabled?: boolean;
  isElectrician?: boolean;
}

export interface OrderStatusEvent {
  id: string;
  status: OrderStatus;
  changedAt: string;
  changedById: string | null;
}

export interface OrderAct {
  id: string;
  actNumber: number;
  materialsCost: string | null;
  cashAmount: string | null;
  nonCashAmount: string | null;
  qrAmount: string | null;
  sentToClientAt: string | null;
  sentTo1cAt: string | null;
  bitrixSyncedAt: string | null;
  publicToken: string;
  pdfPath: string | null;
  receiptPath: string | null;
  updPath: string | null;
  oldStyleActPath: string | null;
  schetDogovorPath: string | null;
  invoicePath?: string | null;
  report?: {
    totalAmount: string;
    notes: string | null;
    /** «Монтажные работы» — доля мастера 30% */
    installationWork?: boolean;
    startedAt: string;
    completedAt: string | null;
    workItems?: WorkItem[];
    photos?: { id: string; filePath: string; phase: 'BEFORE' | 'AFTER'; createdAt: string }[];
  } | null;
}

export interface Order {
  id: string;
  orderNumber?: number;
  bitrixDealId: string | null;
  address: string;
  clientName: string;
  clientPhone: string;
  description: string | null;
  status: OrderStatus;
  scheduledAt: string | null;
  technicianId: string | null;
  technician?: { id: string; fullName: string; phone: string } | null;
  statusEvents?: OrderStatusEvent[];
  createdAt: string;
  updatedAt: string;
  act?: OrderAct | null;
  requestType?: string | null;
  appealType?: string | null;
  paymentType?: string | null;
  additionalContacts?: { name: string; phone: string }[] | null;
  sellerOrganization?: string | null;
  isLegalEntity?: boolean;
  companyName?: string | null;
  companyInn?: string | null;
  companyKpp?: string | null;
  companyOgrn?: string | null;
  companyAddress?: string | null;
  createdByTechnician?: boolean;
  // Просьбы по наряду, на которые диспетчер ещё не ответил (см. OrderRequestsPopup)
  requests?: OrderRequestRef[];
}

export interface WorkItem {
  id: string;
  name: string;
  unit: string;
  quantity: string;
  pricePerUnit: string;
  amount: string;
  isSelfProposed: boolean;
}

export interface Report {
  id: string;
  notes: string | null;
  materialsCost: string | null;
  totalAmount: string;
  /** «Монтажные работы» — доля мастера 30% */
  installationWork?: boolean;
  startedAt: string;
  completedAt: string | null;
  workItems?: WorkItem[];
  technician?: { id: string; fullName: string };
}

export interface Act {
  id: string;
  actNumber: number;
  orderId: string;
  reportId: string;
  pdfPath: string | null;
  receiptPath: string | null;
  updPath: string | null;
  oldStyleActPath: string | null;
  schetDogovorPath: string | null;
  invoicePath?: string | null;
  materialsCost: string | null;
  cashAmount: string | null;
  nonCashAmount: string | null;
  qrAmount: string | null;
  clientPhone: string;
  publicToken: string;
  sentToClientAt: string | null;
  sentTo1cAt: string | null;
  bitrixSyncedAt: string | null;
  createdAt: string;
  order?: Order;
  report?: Report;
}

export interface ChatMessage {
  id: string;
  senderId: string;
  receiverId: string;
  text: string;
  createdAt: string;
  readAt: string | null;
}

export interface ChatThread {
  technician: { id: string; fullName: string };
  lastMessage: ChatMessage | null;
  unreadCount: number;
}

export type ChatChannel = 'general' | 'grease-trap';

export interface ReportMediaFile {
  id: string;
  filePath: string;
  createdAt: string;
}

export interface OrderMediaGroup {
  reportId: string;
  orderId: string;
  address: string;
  clientName: string;
  technicianName: string;
  reportCreatedAt: string;
  mediaUpdatedAt: string;
  media: ReportMediaFile[];
}

export interface GeneralChatMessage {
  id: string;
  channel: ChatChannel;
  senderId: string;
  sender: { id: string; fullName: string };
  text: string;
  createdAt: string;
}

export interface GpsPosition {
  technicianId: string;
  technicianName: string;
  deviceId: string | null;
  lat: number;
  lng: number;
  recordedAt: string;
  // false — телефон не выходил на связь дольше 5 минут; точка на карте остаётся
  // (последнее известное место за смену), но помечается как «не на связи»
  isOnline: boolean;
}

export interface GpsTrail {
  technicianId: string;
  technicianName: string;
  deviceId: string | null;
  points: { lat: number; lng: number; recordedAt: string }[];
}

export interface Earnings {
  completedOrders: number;
  totalRevenue: number;
  commissionRate: number;
  earnings: number;
  sinceLastPayoutAt: string | null;
}

// Отметка «мне выдали зарплату», которую сантехник ставит сам в приложении
export interface Payout {
  id: string;
  technicianId: string;
  amount: string;
  paidAt: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  orderId: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface TechnicianReport {
  id: string;
  technicianId: string;
  technician: { id: string; fullName: string };
  periodType: 'DAILY' | 'WEEKLY' | 'OLD_STYLE';
  periodStart: string;
  periodEnd: string;
  pdfPath: string;
  totalAmount: string | null;
  totalEarnings: string | null;
  createdAt: string;
}

// Ведомость за период (GET /technician-reports/summary) — считается на лету по сданным
// актам, ничего предварительно формировать не нужно. Суммы приходят числами в рублях,
// уже сведёнными: сумма дней равна итогу по сантехнику, сумма сантехников — общему итогу
export interface SummaryDay {
  date: string;
  visits: number;
  revenue: number;
  earnings: number;
}

export interface SummaryTechnician {
  id: string;
  fullName: string;
  isActive: boolean;
  commissionRate: number;
  visits: number;
  revenue: number;
  materials: number;
  earnings: number;
  days: SummaryDay[];
}

// Один акт из периода (GET /technician-reports/acts) — строка, из которой собирается
// и раскрытый день, и раздел по договорам. Суммы те же, что в ведомости
export interface PeriodAct {
  id: string;
  actNumber: string;
  orderNumber: number;
  date: string;
  completedAt: string;
  technicianId: string;
  technicianName: string;
  address: string;
  clientName: string;
  isLegalEntity: boolean;
  companyName: string | null;
  companyInn: string | null;
  payment: 'CASH' | 'NON_CASH' | 'MIXED' | 'UNKNOWN';
  revenue: number;
  materials: number;
  earnings: number;
  /** «Монтажные работы» — доля мастера 30% */
  installationWork?: boolean;
}

export interface PeriodSummary {
  from: string;
  to: string;
  technicians: SummaryTechnician[];
  totals: { visits: number; revenue: number; materials: number; earnings: number };
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  NEW: 'Новый',
  ASSIGNED: 'Назначен',
  EN_ROUTE: 'В пути',
  ARRIVED: 'На месте',
  IN_PROGRESS: 'В работе',
  CLIENT_DECLINED: 'Отказ клиента',
  PAUSED: 'Остановлен',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

export const ORDER_STATUSES_ORDERED: OrderStatus[] = [
  'NEW',
  'ASSIGNED',
  'EN_ROUTE',
  'ARRIVED',
  'IN_PROGRESS',
  'PAUSED',
  'CLIENT_DECLINED',
  'COMPLETED',
  'CANCELLED',
];

// Наряд сам не закрывается и не переносится: сантехник просит, диспетчер решает.
// BITRIX_STAGE_LEFT — сделку в Битриксе увели со стадии «Назначен мастер»
export type OrderRequestType = 'RESCHEDULE' | 'CANCEL' | 'BITRIX_STAGE_LEFT';

export interface OrderRequestRef {
  id: string;
  type: OrderRequestType;
  proposedScheduledAt: string | null;
  reason: string | null;
  bitrixStageName: string | null;
  createdAt: string;
}

export interface OrderRequest extends OrderRequestRef {
  orderId: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'CLOSED';
  bitrixStageId: string | null;
  order: {
    id: string;
    orderNumber: number;
    address: string;
    clientName: string;
    scheduledAt: string | null;
    status: OrderStatus;
    bitrixDealId: string | null;
    technician: { id: string; fullName: string; phone: string } | null;
  };
  requestedBy: { id: string; fullName: string } | null;
}

// Проверка контрагента (клиента-юрлица или ИП) по данным ФНС — см. backend/src/counterparties
export interface CounterpartyProblem {
  code: string;
  text: string;
}

export interface CounterpartyCheck {
  id: string;
  inn: string;
  ourName: string | null;
  ourKpp: string | null;
  found: boolean;
  type: 'LEGAL' | 'INDIVIDUAL' | null;
  name: string | null;
  status: 'ACTIVE' | 'LIQUIDATING' | 'LIQUIDATED' | 'BANKRUPT' | 'REORGANIZING' | null;
  kpp: string | null;
  ogrn: string | null;
  address: string | null;
  manager: string | null;
  liquidationDate: string | null;
  problems: CounterpartyProblem[];
  checkedAt: string;
  problemsChangedAt: string | null;
  lastError: string | null;
}

export interface CounterpartyRunInfo {
  startedAt: string;
  finishedAt: string | null;
  total: number;
  checked: number;
  withProblems: number;
  failed: number;
}

export interface CounterpartyList {
  configured: boolean;
  running: boolean;
  lastRun: CounterpartyRunInfo | null;
  items: CounterpartyCheck[];
}

export const COUNTERPARTY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Действует',
  LIQUIDATING: 'Ликвидируется',
  LIQUIDATED: 'Ликвидирована',
  BANKRUPT: 'Банкротство',
  REORGANIZING: 'Реорганизация',
};

// Справочник: объекты обслуживания, услуги, правила (см. backend/src/kb)
export type KbSection = 'OBJECT' | 'SERVICE' | 'RULE' | 'OTHER';

export const KB_SECTION_LABELS: Record<KbSection, string> = {
  OBJECT: 'Объекты',
  SERVICE: 'Услуги',
  RULE: 'Правила',
  OTHER: 'Прочее',
};

export interface KbFile {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  /** Путь на сервере API с подписью: /kb/files/:id?e=…&s=… */
  url: string;
}

export interface KbArticle {
  id: string;
  section: KbSection;
  title: string;
  body: string;
  tags: string[];
  matchInns: string[];
  matchAddresses: string[];
  matchPhones: string[];
  staffOnly: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  files: KbFile[];
  /** Только в карточках к наряду: почему подошла */
  reason?: string;
}

export interface KbRevision {
  id: string;
  action: string;
  editedByName: string | null;
  createdAt: string;
  snapshot: Pick<KbArticle, 'section' | 'title' | 'body' | 'tags' | 'staffOnly' | 'archived'>;
}

export interface KbSearchResult {
  articles: { id: string; section: KbSection; title: string; snippet: string }[];
  prices: { id: string; name: string; defaultUnit: string; defaultPrice: string | null }[];
}

export interface PriceItem {
  id: string;
  name: string;
  defaultUnit: string;
  defaultPrice: string | null;
  isActive: boolean;
  forElectricians: boolean;
}

// --- Обслуживание: ежемесячные сделки в Битриксе (backend/src/maintenance) ---

export type MaintenanceRunStatus = 'PENDING' | 'CREATED' | 'FAILED' | 'SKIPPED' | 'MANUAL';

export interface MaintenanceDeal {
  id: string;
  kind: string;
  templateDealId: string;
  titleTemplate: string;
  nextTitle: string;
  amount: number;
  amountEditable: boolean;
  productRows: { name: string; price: number; quantity: number }[];
  stageId: string;
  stageName: string;
  brigadeUserId: string | null;
  brigadeName: string | null;
  assignedById: string | null;
  assignedName: string | null;
  masterComment: string | null;
  timeComment: string | null;
  address: string | null;
  periodUnit: 'MONTH' | 'WEEK';
  every: number;
  weekday: number | null;
  leadDays: number;
  createDay: number;
  workDay: number | null;
  workTime: string | null;
  forPreviousMonth: boolean;
  startPeriod: string;
  active: boolean;
  schedule: string;
  current: { key: string; status: MaintenanceRunStatus; dealId: string | null; error: string | null } | null;
  currentKey: string | null;
  next: { key: string; date: string; workDate: string | null } | null;
  contactsCount: number;
  hasRequisites: boolean;
}

export interface MaintenanceObject {
  id: string;
  name: string;
  address: string;
  note: string;
  bitrixCompanyId: string | null;
  companyTitle: string | null;
  deals: MaintenanceDeal[];
}

export interface MaintenanceList {
  configured: boolean;
  period: string;
  today: string;
  objects: MaintenanceObject[];
}

export interface MaintenanceTemplate {
  dealId: string;
  title: string;
  kind: string;
  titleTemplate: string;
  companyId: string | null;
  companyTitle: string | null;
  address: string | null;
  amount: number;
  productRows: { PRODUCT_NAME?: string; PRICE?: number | string; QUANTITY?: number | string }[];
  contacts: unknown[];
  requisiteLink: unknown | null;
  stageId: string;
  masterComment: string | null;
  timeComment: string | null;
  brigadeUserId: string | null;
  assignedById: string | null;
  createdDay: number | null;
  forPreviousMonth: boolean;
  workDay: number | null;
  workTime: string | null;
}

export interface MaintenanceStage {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
}

export interface MaintenanceUser {
  id: string;
  name: string;
}

export interface MaintenanceBrigade {
  id: string;
  name: string;
  active: boolean;
}

export interface MaintenanceHistoryItem {
  period: string;
  status: MaintenanceRunStatus;
  dealId: string | null;
  error: string | null;
  at: string;
  title: string | null;
  amount: number | null;
  stageName: string | null;
  deletedInBitrix: boolean;
}

export interface MaintenanceSuggestion {
  companyId: string;
  companyTitle: string;
  kind: string;
  months: number;
  perMonth: number;
  lastDealId: string;
  lastTitle: string;
  amount: number | null;
  typicalDay: number;
  address: string | null;
  currentMonthDealId: string | null;
  alreadyConfigured: boolean;
}

export interface MaintenancePreview {
  period: string;
  workDate: string | null;
  fields: { code: string; label: string; value: unknown }[];
  contacts: number;
  rows: { name: string; price: number; quantity: number }[];
  requisites: boolean;
}
